import type { NuxtApp } from 'nuxt/app';
import type { RuntimeConfig } from 'nuxt/schema';

import { getClientLinks, getServerLinks } from '@/graphql/utils';
import { captureMessageForSentry, setupSentry } from '@/libraries/sentry';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { ApolloLink } from '@apollo/client';
import { getCurrentScope, setUser } from '@sentry/nuxt';
import { getApps, initializeApp } from 'firebase/app';

export default defineNuxtPlugin({
	name: 'services-setup',
	dependsOn: ['core-env'],
	parallel: true,
	async setup() {
		const context = useNuxtApp();
		const runtimeConfig = useRuntimeConfig();
		const headers = useRequestHeaders();

		const userStore = useUserStore(context.$pinia);
		const appStore = useAppStore(context.$pinia);
		const languageStore = useLanguageStore(context.$pinia);

		// Initialize Sentry early with router to catch all initialization errors
		if (import.meta.client) {
			setupSentry({
				router: context.$router,
				jwId: userStore.jwId,
				xServeTrack: appStore.xServeTrack ?? null,
				isSeoUser: context?.$ssrData?.isSeoUser,
				isLoggedIn: userStore.isLoggedIn,
				buyboxView: userStore.newBuyboxView,
				buyboxExpView: userStore.buyboxExpView,
				buyboxPresentationType: userStore.buyboxPresentationType ?? undefined,
				buyboxTooltipAcknowledged: userStore.newBuyboxTooltipAcknowledged,
				country: languageStore.country,
				newBuyboxFilterType: userStore.newBuyboxFilterType,
				onboardingStatus: userStore.onboardingStatus ?? undefined,
			});
		}

		// Set server-side Sentry context once stores are initialized.
		// Supplements the preHandler cookie-based user set in sentry-server.js —
		// covers SEO/first-time users who have no jw_id cookie yet, and adds web_locale.
		if (import.meta.server) {
			getCurrentScope().setTags({
				web_locale: languageStore.webLocale,
				auth_status: userStore.isLoggedIn ? 'logged_in' : 'logged_out',
			});

			if (userStore.jwId) {
				setUser({ id: userStore.jwId });
			}
		}

		let s = getHrTime();
		initFirebase(runtimeConfig);
		recordSsrPhase(context, 'init-firebase', s);

		if (import.meta.server) {
			// stop the apollo client after the plugin has extracted the cache for the client
			// this prevents OOM issues from cached values still being referenced across requests
			context.hook('app:rendered', () => context.$apolloClients.default?.stop());
		}

		s = getHrTime();
		initApollo({
			appStore,
			context,
			headers,
			runtimeConfig,
		});
		recordSsrPhase(context, 'apollo-links', s);

		if (BUILD_CONFIG.globals.BUILD_TARGET === 'SPA') {
			// initialize id service
			if (userStore.jwId) {
				context.provide('ssrData', {
					...(context.$ssrData ?? {}),
					isSeoUser: false,
				});
				userStore.setJwId(userStore.jwId);
			} else {
				context.provide('ssrData', {
					...(context.$ssrData ?? {}),
					isSeoUser: true,
				});

				await userStore.fetchJwId();
			}
		}
	},
});

function initFirebase(runtimeConfig: RuntimeConfig) {
	if (getApps().length === 0) {
		try {
			initializeApp({
				apiKey: runtimeConfig.public.jwConfig.FIREBASE_API_KEY,
				authDomain: runtimeConfig.public.jwConfig.FIREBASE_AUTH_DOMAIN,
				projectId: runtimeConfig.public.jwConfig.FIREBASE_PROJECT_ID,
				storageBucket: runtimeConfig.public.jwConfig.FIREBASE_STORAGE_BUCKET,
				messagingSenderId: runtimeConfig.public.jwConfig.FIREBASE_MESSAGING_SENDER_ID,
				appId: runtimeConfig.public.jwConfig.FIREBASE_APP_ID,
			});
		} catch (err) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const error: any = err;
			captureMessageForSentry(
				'Firebase Init Issue:',
				{ error, where: '[main.common.ts] initFirebase()' },
				'error'
			);
		}
	}
}

function initApollo({
	appStore,
	context,
	headers,
	runtimeConfig,
}: {
	runtimeConfig: RuntimeConfig;
	headers: Readonly<Record<string, string>>;
	context: NuxtApp;
	appStore: ReturnType<typeof useAppStore>;
}) {
	let links: ApolloLink[] = [];
	if (import.meta.server) {
		/**
		 * Backend Tracking
		 * Forward HTTP request user-agent to apollo requests
		 * Used for Tracking Mutations
		 **/
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const extraHeaders: Record<string, any> = {};

		if (runtimeConfig.rateLimitShortcut != null) {
			extraHeaders['X-Rate-Limit-Shortcut'] = runtimeConfig.rateLimitShortcut;
		}

		const UserAgent = headers['user-agent'];
		if (UserAgent && UserAgent.indexOf('GoogleStackdriverMonitoring-UptimeChecks') === -1) {
			extraHeaders['x-forwarded-user-agent'] = UserAgent;
		}

		const contextXServeTrack = context?.$ssrData?.xServeTrack;
		if (contextXServeTrack) {
			extraHeaders['x-environment'] = contextXServeTrack;
		}

		links = getServerLinks({ pinia: context.$pinia, headers: { ...headers, ...extraHeaders } });
	} else {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const extraHeaders: Record<string, any> = {};
		if (appStore.xServeTrack) {
			extraHeaders['x-environment'] = appStore.xServeTrack;
		}

		links = getClientLinks({ pinia: context.$pinia, headers: { ...headers, ...extraHeaders } });
	}

	const apollo = context.$apolloClients.default;
	if (apollo == null) return;

	apollo.setLink(ApolloLink.from(links));
	apollo.devtoolsConfig.enabled = process.env.NODE_ENV !== 'production';
}
